import React, { useState, useRef } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Check, Edit2, CircleMinus, CircleCheck, Lock } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Toast, { ToastMessage } from "../common/Toast";
import { confirmDelete } from "../common/confirm";
import {
  Agency,
  ProcedureTemplate,
  ProgramTemplate,
  ProgramProcedureMap,
  ProgramTemplateCreatePayload,
} from "../../services/auditService";
import ProgramModal from "./ProgramModal";
import ProcedurePickerModal from "./ProcedurePickerModal";

interface ProgramConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: ProgramTemplate | null;
  agencies: Agency[];
  procedures: ProcedureTemplate[];
  /** Full map rows for the current program (with sequence_no) */
  programMaps: ProgramProcedureMap[];
  onSaveDetails: (data: ProgramTemplateCreatePayload, id?: number) => void | Promise<void>;
  /** Called with reordered map IDs in the new sequence order */
  onAddProcedure: (programId: number, procedureId: number) => void;
  onRemoveProcedure: (mapId: number) => void;
  onReorderProcedure: (mapId: number, newSeq: number) => void;
  onInactivate?: (id: number) => Promise<void>;
  onReactivate?: (id: number) => Promise<void>;
}

type Tab = "Basic Details" | "Procedures";

const ProgramConfigModal: React.FC<ProgramConfigModalProps> = ({
  isOpen,
  onClose,
  program,
  agencies,
  procedures,
  programMaps,
  onSaveDetails,
  onAddProcedure,
  onRemoveProcedure,
  onReorderProcedure,
  onInactivate,
  onReactivate,
}) => {
  const [tab, setTab] = useState<Tab>("Basic Details");
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const editFormRef = useRef<HTMLFormElement>(null);

  // Approved programs are frozen: Basic Details locks (no edit/toggle) and the
  // Procedures actions stay visible but dimmed — clicking shows a lock toast.
  // Unlock via the status dot's Reopen action.
  const PROGRAM_LOCKED_MSG = 'Record is approved and locked. Reopen via the workflow button to edit.';
  const isApproved = (program as any)?.workflow_state === 'approved';
  const blockedByLock = (): boolean => {
    if (isApproved) { setToast({ type: 'error', message: PROGRAM_LOCKED_MSG }); return true; }
    return false;
  };

  // Sort linked maps by sequence_no for display
  const sortedMaps = [...programMaps].sort(
    (a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0),
  );
  const procedureById = Object.fromEntries(procedures.map((p) => [p.id, p]));

  const handleSaveDetails = async (data: ProgramTemplateCreatePayload, id?: number) => {
    setSubmitting(true);
    try {
      await onSaveDetails(data, id);
      setIsEditing(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!program) return;
    const isActive = (program.is_active ?? 1) === 1;
    setToggling(true);
    try {
      if (isActive) {
        await onInactivate?.(program.id);
      } else {
        await onReactivate?.(program.id);
      }
    } finally {
      setToggling(false);
    }
  };

  const handleRemove = async (mapId: number) => {
    const ok = await confirmDelete({
      title: "Remove procedure?",
      text: "This will unlink the procedure from the program template.",
      confirmText: "Yes, Remove",
    });
    if (!ok) return;
    onRemoveProcedure(mapId);
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const above = sortedMaps[idx - 1];
    const current = sortedMaps[idx];
    onReorderProcedure(current.id, above.sequence_no ?? idx);
    onReorderProcedure(above.id, current.sequence_no ?? idx + 1);
  };

  const moveDown = (idx: number) => {
    if (idx === sortedMaps.length - 1) return;
    const below = sortedMaps[idx + 1];
    const current = sortedMaps[idx];
    onReorderProcedure(current.id, below.sequence_no ?? idx + 2);
    onReorderProcedure(below.id, current.sequence_no ?? idx + 1);
  };

  const alreadyLinkedIds = sortedMaps.map((m) => m.procedure_template_id);

  const isDetailsTab = tab === "Basic Details";
  const isActive = (program?.is_active ?? 1) === 1;

  return (
    <>
      <Modal
        isOpen={isOpen && !!program}
        onClose={onClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <Modal.Header>
          {program ? (
            isApproved && isDetailsTab ? (
              <>Viewing (locked) "<span className="item-highlight">{program.audit_program_name}</span>"</>
            ) : (
              <>
                Program Configuration —{" "}
                <span className="item-highlight">{program.audit_program_code}</span>
                {" · "}
                {program.audit_program_name}
              </>
            )
          ) : (
            "Program Configuration"
          )}
        </Modal.Header>

        <Modal.Body>
          {/* Tab nav */}
          <div className="app-modal-tabs">
            {(["Basic Details", "Procedures"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`TAB_program_config_${t.toLowerCase().replace(/\s+/g, '_')}`}
                onClick={() => setTab(t)}
                className={`app-modal-tab${tab === t ? " app-modal-tab--active" : ""}`}
              >
                {t}
                {t === "Procedures" && (
                  <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                    {sortedMaps.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {isDetailsTab && isApproved && (
            <div className="approved-banner" data-testid="BANNER_program_config_locked">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          {isDetailsTab && program && (
            <ProgramModal
              isOpen={true}
              embedded
              onClose={() => {/* handled by parent */}}
              onSave={handleSaveDetails}
              initialData={program}
              agenciesList={agencies}
              disabled={!isEditing}
              hideEmbeddedFooter={true}
              embeddedFormRef={editFormRef}
              onInactivate={onInactivate}
              onReactivate={onReactivate}
            />
          )}

          {tab === "Procedures" && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                  Linked Procedures ({sortedMaps.length})
                </span>
                <Button
                  variant="fill"
                  icon={Plus}
                  type="button"
                  data-testid="BTN_program_config_link_procedure"
                  onClick={() => { if (blockedByLock()) return; setPickerOpen(true); }}
                  title={isApproved ? PROGRAM_LOCKED_MSG : undefined}
                  className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  Link Procedure
                </Button>
              </div>

              <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                {sortedMaps.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-400 italic">
                    No procedures linked yet.
                  </div>
                )}
                {sortedMaps.map((mapRow, idx) => {
                  const proc = procedureById[mapRow.procedure_template_id];
                  return (
                    <div
                      key={mapRow.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="text-xs text-gray-400 mt-0.5 w-6 flex-shrink-0">
                          {mapRow.sequence_no ?? idx + 1}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">
                            {proc?.procedure_name ??
                              `#${mapRow.procedure_template_id}`}
                          </div>
                          {proc?.procedure_type && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                {proc.procedure_type}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          data-testid={`BTN_program_config_row_${mapRow.id}_move_up`}
                          onClick={() => { if (blockedByLock()) return; moveUp(idx); }}
                          disabled={idx === 0}
                          className={`p-1.5 text-gray-400 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700 hover:bg-gray-100'}`}
                          title={isApproved ? PROGRAM_LOCKED_MSG : "Move up"}
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          type="button"
                          data-testid={`BTN_program_config_row_${mapRow.id}_move_down`}
                          onClick={() => { if (blockedByLock()) return; moveDown(idx); }}
                          disabled={idx === sortedMaps.length - 1}
                          className={`p-1.5 text-gray-400 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700 hover:bg-gray-100'}`}
                          title={isApproved ? PROGRAM_LOCKED_MSG : "Move down"}
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          type="button"
                          data-testid={`BTN_program_config_row_${mapRow.id}_remove`}
                          onClick={() => { if (blockedByLock()) return; handleRemove(mapRow.id); }}
                          className={`p-1.5 text-gray-400 rounded ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-600 hover:bg-red-50'}`}
                          title={isApproved ? PROGRAM_LOCKED_MSG : "Remove from program"}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={isDetailsTab && !isApproved ? 'app-modal-footer--split' : ''}>
          {isDetailsTab && !isApproved && (
            <>
              <Button
                variant={isActive ? 'inactive-outline' : 'active-outline'}
                icon={isActive ? CircleMinus : CircleCheck}
                type="button"
                data-testid="BTN_program_config_toggle_active"
                onClick={handleToggleActive}
                disabled={toggling || submitting}
              >
                {toggling ? '...' : isActive ? 'Inactive' : 'Active'}
              </Button>
              {isEditing ? (
                <Button
                  type="button"
                  variant="update"
                  icon={Check}
                  data-testid="BTN_program_config_update"
                  disabled={submitting}
                  onClick={() => editFormRef.current?.requestSubmit()}
                >
                  {submitting ? "Updating..." : "Update"}
                </Button>
              ) : (
                <Button
                  variant="fill"
                  icon={Edit2}
                  data-testid="BTN_program_config_edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>

      {program && (
        <ProcedurePickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          proceduresList={procedures}
          alreadyLinkedIds={alreadyLinkedIds}
          programScope={program.ownership_scope ?? "GRC-Common"}
          programAgencyId={program.agency_id}
          onAdd={(id) => {
            onAddProcedure(program.id, id);
            setPickerOpen(false);
          }}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default ProgramConfigModal;
